import {
  Box,
  Card,
  CircularProgress,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
} from "@mui/material";
import { useParams } from "react-router-dom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import "../../Components/Common/Styles/Formstyles.css";
import Icon from "../../Assets/images/svg/Icons/receipt_FILL1_.svg";
import SearchandExport from "../../Components/Common/SearchandExport/SearchandExport";
import ActionIcon from "../../Assets/images/png/Action.png";
import { GetApiMethod, DeleteApiMethod } from "../../Utils/ApiService";
import BoxHeader from "../../Components/Common/Box/Boxheader";
import { useLocation, useNavigate } from "react-router-dom";
import BannerUploadForm from "./BannerUploadForm";
import SuccessDialog from "../../Components/Common/SuccesMessages/SuccessDialog";
import { useSuccessDialog } from "../../Components/Common/SuccesMessages/SuccessDialog";
interface Banner {
  id: string;
  fileName: string;
    imageUrl?: string;
  fileKey?: string; 
  name?:string;
}

const BannerManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showBannerList, setShowBannerList] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [banners, setBanners] = useState<Banner[]>([]);
  const [formData, setFormData] = useState<any>({});

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBannerId, setSelectedBannerId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 5;
  const { open: successOpen, message, showSuccess, closeSuccess } = useSuccessDialog();
  // const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const navigate = useNavigate();
  const location = useLocation();
  const { id: bannerIdFromParams } = useParams();
const isFetched = useRef(false);
 const isMounted = React.useRef(false);
  useEffect(() => {
    if (isFetched.current) return; // Skip if already fetched once
  isFetched.current = true
    const isEditMode = location.pathname.endsWith("/edit");
    const isAddMode = location.pathname.endsWith("/add");

    if (isAddMode) {
      // Add mode
      setShowBannerList(false);
      setFormData({});
    } else if (bannerIdFromParams && isEditMode) {
      // Edit mode
      const fetchBannerForEdit = async () => {
        setLoading(true);
        try {
          const response = await GetApiMethod(`banner/${bannerIdFromParams}`);
          const banner = response?.data?.data?.banner;
          if (banner) {
            setFormData(banner);
          } else {
            setFormData({});
          }
        } catch (err) {
          console.error("Error fetching banner for edit:", err);
          setFormData({});
        } finally {
          setShowBannerList(false);
          setLoading(false);
        }
      };
      fetchBannerForEdit();
    } else if (bannerIdFromParams) {
      // View single banner (not edit)
      const fetchSingleBanner = async () => {
        setLoading(true);
        try {
          const response = await GetApiMethod(`banner/${bannerIdFromParams}`);
          const banner = response?.data?.data?.banner;
          if (banner) {
            setBanners([banner]);
            setTotalPages(1);
          } else {
            setBanners([]);
          }
        } catch (err) {
          console.error("Error fetching single banner:", err);
          setBanners([]);
        } finally {
          setShowBannerList(true);
          setLoading(false);
        }
      };
      fetchSingleBanner();
    } else {
      // List all banners
      setShowBannerList(true);
      fetchBannerImages();
    }
  }, []);

  const fetchBannerImages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetApiMethod(
        `banner?page=${page}&limit=${limit}&sort=createdAt&sortDirection=DESC&search=${searchQuery}&t=${Date.now()}`
      );

      const data = response?.data?.data?.records || [];
      const total = response?.data?.data?.totalRecords || 1;

      setBanners(data);
      setTotalPages(Math.ceil(total / limit));
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => {
     if (!isMounted.current){
    fetchBannerImages();
    isMounted.current=true;
     }
  }, []);

  const filteredBanners = banners.filter(
    (banner) =>
      banner.fileName &&
      banner.fileName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handlePageChange = (_: any, value: number) => setPage(value);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, bannerId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedBannerId(bannerId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedBannerId(null);
  };

  const handleEdit = () => {
    const bannerToEdit = banners.find((b) => b.id === selectedBannerId);
    if (bannerToEdit) {
      setFormData(bannerToEdit);
      setShowBannerList(false);
      navigate(`/banners/${bannerToEdit.id}/edit`);
    }
    handleCloseMenu();
  };

  const handleDelete = async () => {
    if (!selectedBannerId) return;
    try {
      setLoading(true);
      await DeleteApiMethod(`banner/${selectedBannerId}`);
      setBanners((prev) => prev.filter((b) => b.id !== selectedBannerId));
      showSuccess("Banner Deleted successfully!");

    } catch (error) {
      console.error("Error deleting banner:", error);
    } finally {
      setLoading(false);
      handleCloseMenu();
    }
  };

  return (
    <AuthorizedLayout>
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 3 }}>
        {loading && (
          <Box className="loading-overlay">
            <CircularProgress size={50} thickness={5} />
          </Box>
        )}

        <Card className="card-layout" sx={{ p: { xs: 2, sm: 3 } }}>
          <BoxHeader
            dashboard_name="Banner Management"
            image={Icon}
            button_show={true}
            button_name="New Banner"
            onAdd={() => {
              setFormData({});
              setShowBannerList(false);
              navigate(`/banners/add`);
            }}
            tabs={["All Banners"]}
            onTabChange={(newTabIndex) => setShowBannerList(newTabIndex === 0)}
          />

          {!showBannerList ? (
            <BannerUploadForm
              formData={formData}
              setFormData={setFormData}
              handleBack={() => { setShowBannerList(true); navigate(`/banners`); }}
              onSubmit={() => { setShowBannerList(true); fetchBannerImages(); }}
            />
          ) : (
            <>
              <SearchandExport searchPlaceholder="Type here" onSearchChange={setSearchQuery} />

              <Box sx={{ overflowX: "auto" }}>
                <Table className="table" sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <div className="inner-cell">
                          <Typography className="table-header">
                            Banner Image
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="inner-cell">
                          <Typography className="table-header">
                            Action
                          </Typography>
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBanners.map((banner) => (
                      <TableRow key={banner.id} className="table-border">
                        <TableCell>
                          <div className="inner-cell">
                            {banner.imageUrl ? (
                            <img
                              src={banner.imageUrl}
                              alt={banner.name}
                              style={{
                                width: 50,
                                height: 50,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                          ) : (
                            <Typography className="table-cell muted">
                              No Photo
                            </Typography>
                          )}

                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="inner-cell">
                            <Box display="flex" alignItems="center">
                              <Typography className="table-cell action">
                                Action
                              </Typography>
                              <IconButton
                                onClick={(e) => handleOpenMenu(e, banner.id)}
                              >
                                <img src={ActionIcon} alt="icon" />
                              </IconButton>
                            </Box>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
              </Box>
            </>
          )}
        </Card>

        <SuccessDialog open={successOpen} onClose={closeSuccess} message={message} />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={handleEdit} className="edit-menu-item">Edit</MenuItem>
          <MenuItem onClick={handleDelete} className="delete-menu-item">Delete</MenuItem>
        </Menu>
      </Box>
    </AuthorizedLayout>
  );
};

export default BannerManagement;
